import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IoClose, IoCheckmarkCircle, IoLockClosed } from 'react-icons/io5';
import { FaSpinner } from 'react-icons/fa';
import axios from 'axios';

export default function SetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  
  const [form, setForm] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validToken, setValidToken] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);

  const validateToken = useCallback(async () => {
    try {
      const response = await axios.post('/api/auth/validate-invitation-token', {
        token: token
      });
      
      if (response.data.valid) {
        setValidToken(true);
        setEmployeeData({
          name: response.data.employee_name,
          email: response.data.employee_email
        });
      } else {
        setError(response.data.message || 'Invalid invitation token.');
      }
    } catch (err) {
      setError('Failed to validate invitation token. Please contact your administrator.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. Missing token.');
      setLoading(false);
      return;
    }
    validateToken();
  }, [token, validateToken]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      setIsSubmitting(false);
      return;
    }

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post('/api/auth/set-initial-password', {
        token: token,
        email: email || employeeData?.email,
        password: form.password
      });

      setSuccess(response.data.message);
      setTimeout(() => navigate('/signin'), 3000);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 relative">
          <div className="text-center">
            <FaSpinner className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-blue-900 mb-2">Validating Invitation</h2>
            <p className="text-gray-600">Please wait while we validate your invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!validToken) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 relative">
          <button
            onClick={() => navigate('/signin')}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <IoClose size={24} />
          </button>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <IoClose className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-900 mb-4">Invalid Invitation</h2>
            <p className="text-red-600 mb-6">{error}</p>
            <button 
              onClick={() => navigate('/signin')}
              className="w-full bg-blue-700 text-white py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors"
            >
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 relative">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <IoCheckmarkCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-900 mb-4">Password Set Successfully!</h2>
            <p className="text-green-600 mb-4">{success}</p>
            <p className="text-gray-600 mb-6">You will be redirected to the sign in page in a few seconds...</p>
            <button 
              onClick={() => navigate('/signin')}
              className="w-full bg-blue-700 text-white py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors"
            >
              Sign In Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-100 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 relative">
        {/* Close Button */}
        <button
          onClick={() => navigate('/signin')}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Close"
        >
          <IoClose size={24} />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <IoLockClosed className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-3xl font-bold text-blue-900 mb-2">Set Your Password</h2>
          {employeeData && (
            <div className="bg-blue-50 rounded-lg p-4 text-left">
              <p className="text-blue-900 mb-1">
                Welcome, <span className="font-semibold">{employeeData.name}</span>!
              </p>
              <p className="text-blue-700 text-sm">
                Please set a password for: <span className="font-medium">{employeeData.email}</span>
              </p>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              required
              minLength="8"
              placeholder="Enter your new password (min. 8 characters)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              required
              minLength="8"
              placeholder="Confirm your new password"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-700 text-white py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <FaSpinner className="w-4 h-4 animate-spin mr-2" />
                Setting Password...
              </span>
            ) : (
              'Set Password'
            )}
          </button>
        </form>

        {/* Security Notice */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>Your password will be encrypted and stored securely.</p>
        </div>
      </div>
    </div>
  );
} 